<template>
  <component
    :is="actionable ? 'button' : 'article'"
    class="oms-stat-card"
    :class="[
      variant ? `oms-stat-card--${variant}` : '',
      { 'oms-stat-card--actionable': actionable },
    ]"
    :type="actionable ? 'button' : null"
    @click="handleClick"
  >
    <span class="oms-stat-card__icon">
      <v-icon>{{ icon }}</v-icon>
    </span>
    <span class="oms-stat-card__label">{{ label }}</span>
    <strong class="oms-stat-card__value">{{ value }}</strong>
    <small v-if="caption" class="oms-stat-card__caption">{{ caption }}</small>
    <span v-if="trend" class="oms-stat-card__trend">{{ trend }}</span>
  </component>
</template>

<script>
export default {
  name: "StatCard",
  props: {
    actionable: {
      type: Boolean,
      default: false,
    },
    caption: {
      type: String,
      default: "",
    },
    icon: {
      type: String,
      default: "dashboard",
    },
    label: {
      type: String,
      required: true,
    },
    trend: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number],
      default: "0",
    },
    variant: {
      type: String,
      default: "primary",
    },
  },
  methods: {
    handleClick(event) {
      if (this.actionable) {
        this.$emit("click", event);
      }
    },
  },
};
</script>

<style scoped>
.oms-stat-card {
  background: #ffffff;
  border: 0.0625rem solid var(--oms-border, #ebe9e2);
  border-radius: var(--oms-radius, 1rem);
  box-shadow: var(--oms-shadow, 0 0.0625rem 0.125rem rgba(26, 25, 22, 0.04));
  color: var(--oms-ink, #3a382f);
  column-gap: 1.125rem;
  display: grid;
  grid-template-columns: 2.25rem minmax(0, 1fr);
  grid-template-rows: auto auto auto;
  min-height: 6.25rem;
  min-width: 0;
  padding: 0.875rem;
  position: relative;
  text-align: left;
  width: 100%;
}

.oms-stat-card::before {
  display: none;
}

.oms-stat-card__icon {
  align-items: center;
  background: var(--stat-soft, var(--oms-primary-soft, #fdf0e4));
  border-radius: 0.5rem;
  display: inline-flex;
  grid-row: 1 / span 2;
  height: 2.25rem;
  justify-content: center;
  width: 2.25rem;
}

.oms-stat-card__icon .v-icon {
  color: var(--stat-accent, var(--oms-primary-dark, #d96a10));
  font-size: 1.25rem;
}

.oms-stat-card__label {
  color: var(--oms-muted, #74716a);
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.2;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}

.oms-stat-card__value {
  color: var(--oms-ink-strong, #1a1916);
  display: block;
  font-family: "Geist Mono", ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 1.8125rem;
  font-weight: 700;
  grid-column: 2;
  line-height: 1.05;
  margin-top: 0.3125rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oms-stat-card__caption,
.oms-stat-card__trend {
  color: #9e9a91;
  display: block;
  font-size: 0.6875rem;
  font-weight: 500;
  grid-column: 1 / -1;
  line-height: 1.3;
  margin-top: 0.5rem;
  min-width: 0;
  overflow-wrap: anywhere;
}

.oms-stat-card__trend {
  color: var(--stat-accent, var(--oms-primary-dark, #d96a10));
}

.oms-stat-card--actionable {
  cursor: pointer;
  font: inherit;
  transition:
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.16s ease;
}

.oms-stat-card--actionable:hover,
.oms-stat-card--actionable:focus {
  border-color: var(--stat-accent, var(--oms-primary, #ea7a1c));
  box-shadow: 0 0.75rem 1.5rem rgba(26, 25, 22, 0.1);
  outline: none;
  transform: translateY(-0.0625rem);
}

.oms-stat-card--blue {
  --stat-accent: var(--oms-blue, #2456b8);
  --stat-soft: var(--oms-blue-soft, #eaf1fd);
}

.oms-stat-card--green {
  --stat-accent: var(--oms-green, #12764c);
  --stat-soft: var(--oms-green-soft, #e8f5ee);
}

.oms-stat-card--red {
  --stat-accent: var(--oms-red, #be3b32);
  --stat-soft: var(--oms-red-soft, #fbeae8);
}

.oms-stat-card--yellow {
  --stat-accent: var(--oms-yellow, #b5820f);
  --stat-soft: var(--oms-yellow-soft, #fbf1da);
}
</style>
